import { MassValues } from "./types";

export type CsvFieldByHeader = Record<string, string>;

export function parseCsv(text: string): string[][] {
  const content = text.replace(/^\ufeff/, "");
  const firstLine = content.split(/\r?\n/)[0] ?? "";
  const delimiter =
    (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0)
      ? ";"
      : ",";

  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let insideQuotes = false;

  for (let i = 0; i < content.length; i += 1) {
    const char = content[i];

    if (insideQuotes) {
      if (char === '"') {
        if (content[i + 1] === '"') {
          field += '"';
          i += 1;
        } else insideQuotes = false;
      } else field += char;
      continue;
    }

    if (char === '"') {
      insideQuotes = true;
    } else if (char === delimiter) {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && content[i + 1] === "\n") i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }

  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter((line) => line.some((value) => value.trim() !== ""));
}

export function normalizeHeader(header: string): string {
  return header
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}

export function recordsFromCsv<T extends MassValues>(
  text: string,
  options: {
    fieldByHeader: CsvFieldByHeader;
    emptyValues: T;
    expectedHeaders: string[];
  },
): T[] {
  const { fieldByHeader, emptyValues, expectedHeaders } = options;

  const [headerRow, ...dataRows] = parseCsv(text);
  if (!headerRow) return [];

  const fields = headerRow.map(
    (header) => fieldByHeader[normalizeHeader(header)],
  );

  if (fields.every((field) => !field)) {
    throw new Error(
      `Cabeçalho não reconhecido. Use as colunas: ${expectedHeaders.join(", ")}`,
    );
  }

  return dataRows.map((line) =>
    fields.reduce<T>(
      (record, field, index) => {
        if (field) (record as MassValues)[field] = (line[index] ?? "").trim();
        return record;
      },
      { ...emptyValues },
    ),
  );
}

function escapeCsvValue(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function downloadCsv(fileName: string, lines: string[][]): void {
  const csv = lines
    .map((line) => line.map(escapeCsvValue).join(","))
    .join("\n");

  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
